import { ReactElement } from 'react';
import { RollingDetail, GRAVITY } from '../breakdownMath';
import { formatNumber } from '../breakdownFormat';
import FormulaRow from './FormulaRow';

interface Props {
  detail: RollingDetail;
  duration: number;
  combinedMass: number;
  crr: number; // temperature-corrected Crr (CrrInput_xG5)
  crrCalibration: number; // base Crr from config
  wheelCircumference: number;
}

export default function RollingSection({
  detail,
  duration,
  combinedMass,
  crr,
  crrCalibration,
  wheelCircumference,
}: Props): ReactElement {
  const { distance, revolutions, work, power } = detail;

  return (
    <div className="cr-py-2">
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        Energy dissipated to rolling resistance over the distance covered. Negative because it
        removes energy from the system.
      </p>

      <FormulaRow
        className="cr-mb-4"
        inputs={[
          { header: 'Wheel revolutions', value: `${formatNumber(revolutions, 2)} rev` },
          { header: '× Circumference', value: `${formatNumber(wheelCircumference, 4)} m` },
        ]}
        result={{ header: 'Distance', value: `${formatNumber(distance, 2)} m` }}
      />
      <FormulaRow
        className="cr-mb-4"
        inputs={[
          { header: '−1 ×', value: '−1' },
          { header: 'Distance', value: `${formatNumber(distance, 2)} m` },
          { header: '× Mass', value: `${formatNumber(combinedMass, 1)} kg` },
          { header: '× Gravity', value: `${formatNumber(GRAVITY, 2)} m/s²` },
          {
            header: '× Crr (temperature-corrected)',
            value: `${formatNumber(crr, 5)} (base ${formatNumber(crrCalibration, 5)})`,
          },
        ]}
        result={{ header: 'Rolling work', value: `${formatNumber(work, 0)} J` }}
      />
      <FormulaRow
        inputs={[
          { header: 'Rolling work', value: `${formatNumber(work, 0)} J` },
          { header: '÷ Time', value: `${formatNumber(duration, 2)} s` },
        ]}
        result={{ header: 'Rolling power', value: `${formatNumber(power, 1)} W` }}
      />

      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Distance is read from the per-traverse <code>WhlLdistDiff_sG3</code> channel
        (= <code>WhlRrevDiff_NG3 × wheel_circumference</code>). Crr is temperature-corrected
        per traverse via <code>CrrInput_xG5 = crr_temp_compensation.crr_at_temp(crr, Temp_TG5)</code>;
        the base <code>crr</code> in parentheses is the calibration value for reference.
      </p>
    </div>
  );
}
